// btw-front/src/components/admin/dashboard/SignupsTab.jsx
// [파트너 가입 신청] 탭 — 신청 큐 상세·승인·반려
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { Badge } from "../../ui/Badge";
import { SignupKindBadge } from "./SignupKindBadge";
import { EmptyState } from "../../ui/EmptyState";
import { Input } from "../../ui/Input";
import * as adminSignups from "../../../api/adminSignups";
import { filterByKeyword } from "../../../lib/keywordFilter";
import { useQueryState } from "../../../lib/useQueryState";
import SignupDetailModal from "./SignupDetailModal";
import SignupRejectModal from "./SignupRejectModal";

// onPendingCountChange — 대시보드 탭 라벨의 대기 건수 배지를 갱신한다. 목록은 전 상태를 받으므로
// 그중 pending 만 센다(검색어와 무관한 전체 건수).
export default function SignupsTab({ onPendingCountChange }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // 키워드(회사명·담당자) — URL 에 보관. 승인·반려 후 목록을 다시 받아도 검색어는 유지된다
  const [q, setQ] = useQueryState("q");
  const visible = useMemo(
    () => filterByKeyword(items, q, ["companyName", "contactName"]),
    [items, q],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminSignups
      .list()
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
        if (onPendingCountChange)
          onPendingCountChange(rows.filter((r) => r.status === "pending").length);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, onPendingCountChange]);

  function statusTone(s) {
    if (s === "approved") return "approved";
    if (s === "rejected") return "rejected";
    return "pending";
  }
  function statusLabel(s) {
    if (s === "approved") return t("admin.signup.statusApproved");
    if (s === "rejected") return t("admin.signup.statusRejected");
    return t("admin.signup.statusPending");
  }

  async function openSignup(item) {
    try {
      const full = await adminSignups.get(item.id);
      setSelected(full);
    } catch (e) {
      message.error(t("admin.signup.toasts.detailLoadFailed"));
    }
  }

  async function handleApprove() {
    if (!selected || acting) return;
    setActing(true);
    try {
      await adminSignups.approve(selected.id);
      message.success(t("admin.signup.toasts.approved"));
      setSelected(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      message.error(
        (e && e.payload && e.payload.error && e.payload.error.message) ||
          t("admin.signup.toasts.approveFailed"),
      );
    } finally {
      setActing(false);
    }
  }

  async function handleReject() {
    if (!selected || acting || !rejectReason.trim()) return;
    setActing(true);
    try {
      await adminSignups.reject(selected.id, rejectReason);
      message.success(t("admin.signup.toasts.rejected"));
      setRejectOpen(false);
      setSelected(null);
      setRejectReason("");
      setReloadKey((k) => k + 1);
    } catch (e) {
      message.error(
        (e && e.payload && e.payload.error && e.payload.error.message) ||
          t("admin.signup.toasts.rejectFailed"),
      );
    } finally {
      setActing(false);
    }
  }

  async function handleDownload(kind = "brochure") {
    if (!selected) return;
    const nameMap = {
      brochure: selected.brochureOriginalName,
      businessRegistration: selected.businessRegistrationOriginalName,
      bankbook: selected.bankbookOriginalName,
    };
    const presentMap = {
      brochure: selected.hasBrochure,
      businessRegistration: selected.hasBusinessRegistration,
      bankbook: selected.hasBankbook,
    };
    if (!presentMap[kind]) return;
    try {
      await adminSignups.downloadAttachment(selected.id, nameMap[kind], kind);
    } catch (e) {
      message.error(e.message || t("admin.signup.toasts.downloadFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <Input
        name="search"
        placeholder={t("admin.signup.searchPlaceholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full sm:w-72"
      />
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        ) : visible.length === 0 ? (
          <EmptyState title={items.length > 0 ? t("admin.signup.searchEmpty") : t("admin.empty")} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">{t("admin.signup.company")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("admin.signup.contact")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("admin.signup.email")}</th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.signup.submittedAt")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">{t("admin.signup.status")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("admin.signup.type")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => openSignup(s)}
                  className="cursor-pointer transition hover:bg-slate-50"
                >
                  <td className="px-4 py-2.5 font-medium text-slate-900">{s.companyName}</td>
                  <td className="px-4 py-2.5 text-slate-700">{s.contactName}</td>
                  <td className="px-4 py-2.5 text-slate-600">{s.contactEmail}</td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {s.createdAt && s.createdAt.slice(0, 10)}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <SignupKindBadge signup={s} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <SignupDetailModal
        acting={acting}
        handleApprove={handleApprove}
        handleDownload={handleDownload}
        selected={selected}
        setRejectOpen={setRejectOpen}
        setSelected={setSelected}
      />

      <SignupRejectModal
        acting={acting}
        handleReject={handleReject}
        rejectOpen={rejectOpen}
        rejectReason={rejectReason}
        setRejectOpen={setRejectOpen}
        setRejectReason={setRejectReason}
      />
    </div>
  );
}
